import * as React from "react";
import { Dialog, Tabs } from "@/components/ui";
import { ProfileTab } from "./ProfileTab";
import { SecurityTab } from "./SecurityTab";
import { NotificationSubscriptionsSection } from "@/components/AssistantForm/components/NotificationSubscriptionsSection";
import { User, ShieldCheck, Lock, Bell } from "lucide-react";
import { useSocialCoachSession } from "@/utils";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultTab?: "profile" | "security" | "notifications";
}

export const UserSettingsDialog: React.FC<Props> = ({ open, onOpenChange, defaultTab = "profile" }) => {
  const { isImpersonating, userId, isEditor } = useSocialCoachSession();
  const [activeTab, setActiveTab] = React.useState<string>(defaultTab);

  React.useEffect(() => {
    if (open) {
      setActiveTab(isEditor ? "profile" : defaultTab);
    }
  }, [open, defaultTab, isEditor]);

  const renderSecurityTabContent = () => {
    if (isImpersonating) {
      return (
        <div className="h-full flex flex-col items-center justify-center p-8 text-center bg-slate-50/50 border border-slate-200/60 rounded-2xl relative overflow-hidden min-h-[400px]">
          {/* Ambient background glows */}
          <div className="absolute h-64 w-64 rounded-full bg-rose-50 -top-12 -left-12 blur-3xl" />

          <div className="relative flex flex-col items-center max-w-sm space-y-6 z-10">
            <div className="h-16 w-16 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center shadow-inner">
              <Lock className="h-8 w-8" />
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-bold text-slate-800">Security Access Locked</h3>
              <p className="text-sm text-slate-500 leading-relaxed">
                For your safety and the user's protection, <b>Security and Multi-Factor Authentication settings</b>{" "}
                cannot be changed while viewing another user's account.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return <SecurityTab />;
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="xl"
      className="max-h-[min(800px,calc(100dvh-2rem))] flex flex-col"
    >
      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
        <Dialog.Content className="flex-1 flex flex-col p-0 overflow-hidden">
          <Dialog.Header className="px-6 pt-4 flex flex-col gap-0 pb-0">
            <Dialog.Title className="text-xl font-bold text-slate-800 mb-4">Account Settings</Dialog.Title>
            {!isEditor && (
              <Tabs.List variant="menu" className="h-12 gap-6">
                <Tabs.Trigger
                  value="profile"
                  className="px-0 h-12 gap-2 data-active:border-blue-500 data-active:text-slate-800 transition-colors"
                >
                  <User className="h-4 w-4" />
                  <span>Profile</span>
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="notifications"
                  className="px-0 h-12 gap-2 data-active:border-blue-500 data-active:text-slate-800 transition-colors"
                >
                  <Bell className="h-4 w-4" />
                  <span>Notifications</span>
                </Tabs.Trigger>
                <Tabs.Trigger
                  value="security"
                  className="px-0 h-12 gap-2 data-active:border-blue-500 data-active:text-slate-800 transition-colors"
                >
                  <ShieldCheck className="h-4 w-4" />
                  <span>Security & MFA</span>
                </Tabs.Trigger>
              </Tabs.List>
            )}
          </Dialog.Header>

          <div className="flex-1 overflow-y-auto p-6">
            <Tabs.Content value="profile" className="m-0 h-full outline-none">
              <ProfileTab />
            </Tabs.Content>
            {!isEditor && (
              <>
                <Tabs.Content value="notifications" className="m-0 h-full outline-none">
                  {userId && (
                    <NotificationSubscriptionsSection userId={userId} isDisabled={false} isOwnProfile={true} />
                  )}
                </Tabs.Content>
                <Tabs.Content value="security" className="m-0 h-full outline-none">
                  {renderSecurityTabContent()}
                </Tabs.Content>
              </>
            )}
          </div>
        </Dialog.Content>
      </Tabs>
    </Dialog>
  );
};
